import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { LogIn, LogOut, MapPin, Clock, Users as UsersIcon, ShieldCheck, Download, Printer, AlertTriangle } from "lucide-react";
import { printReport } from "@/lib/printReport";
import { usePrintTemplate } from "@/hooks/usePrintTemplate";

const fmtTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
};

const downloadCsv = (filename, rows) => {
  const csv = rows.map(r => r.map(c => {
    const s = String(c ?? "");
    return s.includes(",") || s.includes("\"") || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s;
  }).join(",")).join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
};

const captureLocation = () => new Promise((resolve) => {
  if (!navigator.geolocation) return resolve(null);
  navigator.geolocation.getCurrentPosition(
    (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy_m: pos.coords.accuracy }),
    () => resolve(null),
    { enableHighAccuracy: false, timeout: 4000, maximumAge: 60000 }
  );
});

export default function Attendance() {
  const { lang } = useLanguage();
  const template = usePrintTemplate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "manager";

  const [mine, setMine] = useState({ events: [], latest_type: null, latest_ts: null, employee: null });
  const [busy, setBusy] = useState(false);
  const [shareLoc, setShareLoc] = useState(true);

  // Admin filters (Completion pass)
  const [fromDate, setFromDate] = useState(new Date().toISOString().slice(0, 10));
  const [toDate, setToDate] = useState(new Date().toISOString().slice(0, 10));
  const [adminEmpFilter, setAdminEmpFilter] = useState("");
  const [employees, setEmployees] = useState([]);
  const [adminList, setAdminList] = useState({ events: [], count: 0 });

  const loadMine = useCallback(async () => {
    try { const r = await api.get("/attendance/me"); setMine(r.data); }
    catch { /* user not linked; show banner */ }
  }, []);
  const loadAdmin = useCallback(async () => {
    if (!isAdmin) return;
    const params = new URLSearchParams();
    if (fromDate) params.set("from_date", fromDate);
    if (toDate) params.set("to_date", toDate);
    if (adminEmpFilter) params.set("employee_id", adminEmpFilter);
    const r = await api.get(`/attendance?${params.toString()}`);
    setAdminList(r.data);
  }, [fromDate, toDate, adminEmpFilter, isAdmin]);
  const loadEmployees = useCallback(async () => {
    if (!isAdmin) return;
    try { const r = await api.get("/employees"); setEmployees(r.data); } catch { /* ignore */ }
  }, [isAdmin]);

  useEffect(() => { loadMine(); }, [loadMine]);
  useEffect(() => { loadAdmin(); }, [loadAdmin]);
  useEffect(() => { loadEmployees(); }, [loadEmployees]);

  const exportCsv = () => {
    const rows = [["Date", "Time", "Employee", "Type", "Branch", "Lat", "Lng", "Accuracy(m)", "Note"]];
    for (const e of adminList.events) {
      rows.push([(e.date || "").slice(0, 10), (e.ts || "").slice(11, 19), e.employee_name || e.employee_id, e.type, e.branch_id || "", e.lat ?? "", e.lng ?? "", e.accuracy_m ?? "", e.note || ""]);
    }
    downloadCsv(`attendance_${fromDate}_${toDate}.csv`, rows);
    toast.success(lang === "ar" ? "تم التصدير" : "Exported");
  };
  const printReportPro = () => {
    // Aggregate by employee for a clean report
    const byEmp = new Map();
    let totalLate = 0;
    let totalOOR = 0;
    for (const e of adminList.events) {
      const key = e.employee_id;
      if (!byEmp.has(key)) byEmp.set(key, { name: e.employee_name || e.employee_id, ins: 0, outs: 0, late: 0, oor: 0, last_ts: "" });
      const a = byEmp.get(key);
      if (e.type === "in") a.ins += 1;
      if (e.type === "out") a.outs += 1;
      if (e.late_minutes && e.late_minutes > 0) { a.late += e.late_minutes; totalLate += e.late_minutes; }
      if (e.out_of_range) { a.oor += 1; totalOOR += 1; }
      if (!a.last_ts || (e.ts || "") > a.last_ts) a.last_ts = e.ts;
    }
    printReport({
      title: lang === "ar" ? "تقرير الحضور والانصراف" : "Attendance Report",
      lang,
      template,
      meta: [
        { label: lang === "ar" ? "الفترة" : "Period", value: `${fromDate} → ${toDate}` },
        { label: lang === "ar" ? "الموظف" : "Employee", value: adminEmpFilter ? (employees.find(x => x.employee_id === adminEmpFilter)?.full_name || adminEmpFilter) : (lang === "ar" ? "الكل" : "All") },
        { label: lang === "ar" ? "عدد الأحداث" : "Events", value: String(adminList.count) },
        { label: lang === "ar" ? "إجمالي التأخير (دقيقة)" : "Total late (min)", value: String(totalLate) },
        { label: lang === "ar" ? "خارج النطاق" : "Out of range", value: String(totalOOR) },
      ],
      columns: [
        lang === "ar" ? "التاريخ" : "Date",
        lang === "ar" ? "الوقت" : "Time",
        lang === "ar" ? "الموظف" : "Employee",
        lang === "ar" ? "النوع" : "Type",
        lang === "ar" ? "الفرع" : "Branch",
        lang === "ar" ? "التأخير" : "Late",
        lang === "ar" ? "المسافة (م)" : "Distance (m)",
        lang === "ar" ? "الحالة" : "Status",
      ],
      rows: adminList.events.map(e => {
        const isIn = e.type === "in";
        const oor = !!e.out_of_range;
        const late = e.late_minutes || 0;
        const typeBadge = isIn
          ? `<span class="badge badge-ok">${lang === "ar" ? "دخول" : "In"}</span>`
          : `<span class="badge badge-warn">${lang === "ar" ? "خروج" : "Out"}</span>`;
        let statusBadge;
        if (oor) statusBadge = `<span class="badge badge-bad">${lang === "ar" ? "خارج النطاق" : "Out of range"}</span>`;
        else if (late > 0) statusBadge = `<span class="badge badge-warn">${lang === "ar" ? "متأخر" : "Late"}</span>`;
        else statusBadge = `<span class="badge badge-ok">${lang === "ar" ? "ضمن النطاق" : "OK"}</span>`;
        return [
          { value: e.date || (e.ts || "").slice(0, 10), className: "muted num" },
          { value: (e.ts || "").slice(11, 16), className: "muted num" },
          e.employee_name || e.employee_id,
          { value: typeBadge, html: true },
          e.branch_name || e.branch_id || "—",
          { value: isIn && late > 0 ? `${late} ${lang === "ar" ? "د" : "min"}` : "—", className: late > 0 ? "num neg" : "num muted" },
          { value: e.distance_m != null ? String(Math.round(e.distance_m)) : "—", className: oor ? "num neg" : "num muted" },
          { value: statusBadge, html: true },
        ];
      }),
      totals: [
        { label: lang === "ar" ? "أحداث" : "Events", value: String(adminList.count) },
        { label: lang === "ar" ? "تأخير (د)" : "Late (min)", value: String(totalLate), className: totalLate > 0 ? "rose" : "" },
        { label: lang === "ar" ? "خارج النطاق" : "Out of range", value: String(totalOOR), className: totalOOR > 0 ? "rose" : "" },
      ],
      footerNote: lang === "ar" ? "النطاق محسوب وفق نطاق الفرع الجغرافي" : "Out-of-range computed using the branch geofence",
    });
  };

  const punch = async (type) => {
    setBusy(true);
    try {
      const body = {};
      if (shareLoc) {
        const loc = await captureLocation();
        if (loc) body.location = loc;
      }
      const path = type === "in" ? "/attendance/check-in" : "/attendance/check-out";
      await api.post(path, body);
      toast.success(lang === "ar" ? (type === "in" ? "تم تسجيل الدخول" : "تم تسجيل الخروج") : (type === "in" ? "Checked in" : "Checked out"));
      loadMine(); loadAdmin();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const nextType = mine.latest_type === "in" ? "out" : "in";
  const canSelfPunch = !!mine.employee;

  return (
    <div className="space-y-6" data-testid="attendance-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-heading text-4xl gold-text">{lang === "ar" ? "الحضور والانصراف" : "Attendance"}</h1>
        <div className="text-xs text-[var(--text-muted)] flex items-center gap-1">
          <ShieldCheck size={12}/>{lang === "ar" ? "إصدار خفيف: بدون تتبع متواصل" : "Lite: no continuous tracking"}
        </div>
      </div>

      {/* My check-in/out card */}
      {canSelfPunch ? (
        <div className="card-surface p-6 flex flex-wrap items-center justify-between gap-4" data-testid="punch-card">
          <div>
            <div className="text-xs text-[var(--text-muted)] uppercase mb-1">{lang === "ar" ? "أنت الآن" : "You are"}</div>
            <div className="font-heading text-2xl text-[var(--text)]">{mine.employee?.name}</div>
            <div className="text-sm text-[var(--text-muted)] mt-1 flex items-center gap-2">
              <Clock size={13}/>
              {mine.latest_type
                ? (lang === "ar"
                    ? `آخر سجل: ${mine.latest_type === "in" ? "دخول" : "خروج"} في ${fmtTime(mine.latest_ts)}`
                    : `Last: ${mine.latest_type === "in" ? "in" : "out"} at ${fmtTime(mine.latest_ts)}`)
                : (lang === "ar" ? "لا يوجد سجل بعد اليوم" : "No record yet")}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-xs text-[var(--text-muted)] cursor-pointer">
              <input type="checkbox" checked={shareLoc} onChange={e => setShareLoc(e.target.checked)} data-testid="share-loc-toggle"/>
              <MapPin size={12}/>{lang === "ar" ? "مشاركة الموقع (اختياري)" : "Share location (optional)"}
            </label>
            <button onClick={() => punch("in")} disabled={busy || mine.latest_type === "in"}
              data-testid="check-in-btn"
              className="btn-gold px-5 py-2.5 rounded-md flex items-center gap-2 disabled:opacity-40">
              <LogIn size={16}/>{lang === "ar" ? "دخول" : "Check in"}
            </button>
            <button onClick={() => punch("out")} disabled={busy || mine.latest_type !== "in"}
              data-testid="check-out-btn"
              className="btn-rose-outline px-5 py-2.5 rounded-md flex items-center gap-2 disabled:opacity-40">
              <LogOut size={16}/>{lang === "ar" ? "خروج" : "Check out"}
            </button>
          </div>
        </div>
      ) : (
        <div className="card-surface p-4 text-sm text-[var(--text-muted)]">
          {lang === "ar"
            ? "حسابك غير مربوط بسجل موظف. تواصل مع المدير لربط حسابك حتى تتمكن من تسجيل الحضور."
            : "Your account is not linked to an employee record. Ask your manager to link them."}
        </div>
      )}

      {/* My recent events */}
      {canSelfPunch && (
        <div className="card-surface p-5">
          <h2 className="font-heading text-lg mb-3">{lang === "ar" ? "سجلاتي الأخيرة" : "My Recent Records"}</h2>
          <table className="table-dark">
            <thead><tr>
              <th>{lang === "ar" ? "الوقت" : "Time"}</th>
              <th>{lang === "ar" ? "النوع" : "Type"}</th>
              <th>{lang === "ar" ? "الموقع" : "Location"}</th>
              <th>{lang === "ar" ? "ملاحظات" : "Note"}</th>
            </tr></thead>
            <tbody>
              {mine.events.slice(0, 20).map(e => (
                <tr key={e.attendance_id}>
                  <td className="num-display text-[var(--text-muted)]">{(e.ts || "").slice(0, 16).replace("T", " ")}</td>
                  <td>{e.type === "in"
                    ? <span className="badge badge-green flex items-center gap-1 w-fit"><LogIn size={11}/>{lang === "ar" ? "دخول" : "In"}</span>
                    : <span className="badge badge-rose flex items-center gap-1 w-fit"><LogOut size={11}/>{lang === "ar" ? "خروج" : "Out"}</span>}</td>
                  <td className="text-[var(--text-muted)] num-display text-xs">
                    {e.lat != null ? `${e.lat.toFixed(4)}, ${e.lng.toFixed(4)}` : "—"}
                  </td>
                  <td className="text-[var(--text-muted)]">{e.note || "-"}</td>
                </tr>
              ))}
              {mine.events.length === 0 && <tr><td colSpan={4} className="text-center text-[var(--text-muted)] py-6">{lang === "ar" ? "لا توجد سجلات" : "No records"}</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* Admin view */}
      {isAdmin && (
        <div className="card-surface p-5">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2 no-print">
            <h2 className="font-heading text-lg flex items-center gap-2"><UsersIcon size={18}/>{lang === "ar" ? "سجل الحضور" : "Attendance Log"}</h2>
            <div className="flex items-center gap-2 flex-wrap">
              <div>
                <label className="text-[10px] text-[var(--text-muted)] block">{lang === "ar" ? "من" : "From"}</label>
                <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="input-dark text-xs" data-testid="att-from"/>
              </div>
              <div>
                <label className="text-[10px] text-[var(--text-muted)] block">{lang === "ar" ? "إلى" : "To"}</label>
                <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="input-dark text-xs" data-testid="att-to"/>
              </div>
              <div>
                <label className="text-[10px] text-[var(--text-muted)] block">{lang === "ar" ? "الموظف" : "Employee"}</label>
                <select value={adminEmpFilter} onChange={e => setAdminEmpFilter(e.target.value)} className="input-dark text-xs" data-testid="att-emp-filter">
                  <option value="">{lang === "ar" ? "الكل" : "All"}</option>
                  {employees.map(e => <option key={e.employee_id} value={e.employee_id}>{e.full_name || e.full_name_en}</option>)}
                </select>
              </div>
              <button onClick={exportCsv} data-testid="att-export"
                className="px-3 py-1.5 rounded-md text-xs border border-[var(--border)] hover:border-[var(--gold)] bg-[var(--surface)] text-[var(--text)] flex items-center gap-1 mt-4">
                <Download size={12}/>CSV
              </button>
              <button onClick={printReportPro} data-testid="att-print"
                className="px-3 py-1.5 rounded-md text-xs border border-[var(--border)] hover:border-[var(--gold)] bg-[var(--surface)] text-[var(--text)] flex items-center gap-1 mt-4">
                <Printer size={12}/>{lang === "ar" ? "طباعة" : "Print"}
              </button>
            </div>
          </div>
          <div className="text-xs text-[var(--text-muted)] mb-2 num-display">{adminList.count} {lang === "ar" ? "حدث" : "events"}</div>
          <table className="table-dark">
            <thead><tr>
              <th>{lang === "ar" ? "التاريخ" : "Date"}</th>
              <th>{lang === "ar" ? "الوقت" : "Time"}</th>
              <th>{lang === "ar" ? "الموظف" : "Employee"}</th>
              <th>{lang === "ar" ? "النوع" : "Type"}</th>
              <th>{lang === "ar" ? "الفرع" : "Branch"}</th>
              <th>{lang === "ar" ? "التأخير" : "Late"}</th>
              <th>{lang === "ar" ? "الموقع" : "Location"}</th>
              <th>{lang === "ar" ? "الحالة" : "Status"}</th>
            </tr></thead>
            <tbody>
              {adminList.events.map(e => {
                const oor = !!e.out_of_range;
                const late = e.late_minutes || 0;
                return (
                  <tr key={e.attendance_id} data-testid={`att-event-${e.attendance_id}`}>
                    <td className="num-display text-[var(--text-muted)]">{(e.date || (e.ts || "").slice(0, 10))}</td>
                    <td className="num-display text-[var(--text-muted)]">{(e.ts || "").slice(11, 16)}</td>
                    <td className="text-[var(--text)] font-medium">{e.employee_name || e.employee_id}</td>
                    <td>{e.type === "in"
                      ? <span className="badge badge-green">{lang === "ar" ? "دخول" : "In"}</span>
                      : <span className="badge badge-rose">{lang === "ar" ? "خروج" : "Out"}</span>}</td>
                    <td className="text-[var(--text-muted)]">{e.branch_name || e.branch_id || "-"}</td>
                    <td className={`num-display ${late > 0 ? "rose-text font-medium" : "text-[var(--text-muted)]"}`}>
                      {e.type === "in" && late > 0 ? `${late} ${lang === "ar" ? "د" : "min"}` : "—"}
                    </td>
                    <td className="text-[var(--text-muted)] num-display text-xs">
                      {e.lat != null
                        ? <a href={`https://maps.google.com/?q=${e.lat},${e.lng}`} target="_blank" rel="noreferrer" className="gold-text underline">
                            {e.lat.toFixed(4)}, {e.lng.toFixed(4)}
                            {e.distance_m != null && <span className="ms-1 opacity-70">({Math.round(e.distance_m)}م)</span>}
                          </a>
                        : "—"}
                    </td>
                    <td>
                      {oor
                        ? <span className="badge badge-red flex items-center gap-1 w-fit" data-testid={`oor-${e.attendance_id}`}><AlertTriangle size={10}/>{lang === "ar" ? "خارج النطاق" : "Out of range"}</span>
                        : late > 0
                          ? <span className="badge badge-rose">{lang === "ar" ? "متأخر" : "Late"}</span>
                          : <span className="badge badge-green">{lang === "ar" ? "OK" : "OK"}</span>}
                    </td>
                  </tr>
                );
              })}
              {adminList.events.length === 0 && <tr><td colSpan={8} className="text-center text-[var(--text-muted)] py-6">{lang === "ar" ? "لا توجد سجلات" : "No records"}</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
